/**
 * DynamicMapGenerator - Replaced with new terrain algorithm (strict 2x2 water + ocean ring + connectivity fix)
 * Keeps the same public API and types; no dependency on SeasonalAssetManager.
 */

import { 
  TerrainType, 
  MapGenerationOptions, 
  MapConfigAdapter 
} from '../config/MapConfigAdapter';

// 基础地形类型
export type BaseTerrainType = 'PLAIN' | 'WATER' | 'SAND';
export type OverlayTerrainType = 'NONE' | 'FOREST01' | 'FOREST02' | 'MOUNTAIN' | 'BUILDING01' | 'BUILDING02' | 'FENCE';

// 地图生成结果
export interface MapGenerationResult {
  baseLayer: BaseTerrainType[][];
  overlayLayer: OverlayTerrainType[][];
  seed: number;
  connectivity: {
    passableRatio: number;
    largestComponentRatio: number;
  };
  phaserData: {
    baseTileIndices: number[];            // flattened row-major
    overlayTileIndices: number[];         // flattened row-major (-1 for NONE)
    tilesetMapping: {
      base: { [key: string]: string };
      overlay: { [key: string]: string };
    };
  };
  metrics: {
    waterPct: number;
    sandPct: number;
    forest01Pct: number;
    forest02Pct: number;
    mountainPct: number;
    building01Pct: number;
    building02Pct: number;
    fencePct: number;
    passableLargestRatio: number;
  };
}

// Phaser3索引映射结果（预留）
export interface PhaserIndexMapping {
  baseIndices: number[][];
  overlayIndices: number[][];
}

// 索引枚举（用于导出phaserData）
export enum BaseTerrainIndex { PLAIN = 0, WATER = 1, SAND = 2 }
export enum OverlayTerrainIndex { NONE = -1, FOREST01 = 0, FOREST02 = 1, MOUNTAIN = 2, BUILDING01 = 3, BUILDING02 = 4, FENCE = 5 }

// RNG
type RNG = () => number;

export class DynamicMapGenerator {
  private rng: RNG;

  constructor() {
    this.rng = this.mulberry32(Date.now());
  }

  generate(options?: Partial<MapGenerationOptions>): MapGenerationResult {
    const opts = MapConfigAdapter.validateGenerationOptions(options || {});
    const W = Math.max(8, Math.min(opts.width, 100));
    const H = Math.max(8, Math.min(opts.height, 100));
    const seed = opts.seed ?? Date.now();
    this.rng = this.mulberry32(seed);

    // 参数 (从配置获取，避免硬编码)
    const MAX_INLAND_WATER = 0.20;
    const MAX_SAND = 0.20;
    const smoothness = opts.smoothness || 3;
    const waterDensity = opts.waterDensity || opts.baseDensity || 0.40; // 匹配HTML默认40%

    // 1) 生成水域（严格2x2 + 自动回退 + 禁止单tile宽度）
    let water = this.genMaskCA(W, H, waterDensity, smoothness);
    water = this.removeSmallRegions(water, 1); // 保留水域连通块
    water = this.enforceMin2x2(water);
    water = this.removeNarrowRivers(water); // 🆕 移除单tile宽度河流
    // 自动回退以满足内陆覆盖率
    let attemptsW = 0; let densityW = waterDensity;
    while ((this.inlandCoverage(water) > MAX_INLAND_WATER) && attemptsW < 20) {
      attemptsW++;
      densityW = Math.max(0.05, densityW - 0.03);
      water = this.genMaskCA(W, H, densityW, smoothness);
      water = this.removeSmallRegions(water, 0);
      water = this.enforceMin2x2(water);
      water = this.removeNarrowRivers(water); // 🆕 移除单tile宽度河流
    }

    // 2) 海洋外圈 + 内侧隔离带（避免与内陆连通）
    this.addOceanRing(water);

    // 3) 生成沙地（不与水重叠，严格2x2 + 自动回退 + 禁止单tile宽度）
    const sandDensity = opts.sandDensity || opts.baseDensity || 0.40; // 匹配HTML默认40%
    let sand = this.genMaskCA(W, H, sandDensity, smoothness);
    sand = this.maskOut(sand, water); // 不覆盖水
    sand = this.removeSmallRegions(sand, 1); // 保留沙地连通块
    sand = this.enforceMin2x2(sand);
    sand = this.removeNarrowRivers(sand); // 🆕 移除单tile宽度沙地
    let attemptsS = 0; let densityS = sandDensity;
    while ((this.coverage(sand) > MAX_SAND) && attemptsS < 20) {
      attemptsS++;
      densityS = Math.max(0.03, densityS - 0.02);
      sand = this.genMaskCA(W, H, densityS, smoothness);
      sand = this.maskOut(sand, water);
      sand = this.removeSmallRegions(sand, 0);
      sand = this.enforceMin2x2(sand);
      sand = this.removeNarrowRivers(sand); // 🆕 移除单tile宽度沙地
    }

    // 4) Base合成
    const base: BaseTerrainType[][] = this.make2D(H, W, 'PLAIN');
    for (let y=0;y<H;y++) for (let x=0;x<W;x++) {
      if (water[y][x] === 1) base[y][x] = 'WATER';
      else if (sand[y][x] === 1) base[y][x] = 'SAND';
      else base[y][x] = 'PLAIN';
    }

    // 5) Overlay 生成（仅在 PLAIN 上）
    const overlay: OverlayTerrainType[][] = this.make2D(H, W, 'NONE');
    this.placeOverlays(base, overlay);

    // 6) 连通性修复：确保 passable = (PLAIN|SAND) 且非 MOUNTAIN 的整体连通
    this.fixConnectivity(base, overlay);

    // 7) 指标 & phaserData
    const connectivity = this.computeConnectivity(base, overlay);
    const phaserData = this.buildPhaserData(base, overlay);
    const metrics = this.computeMetrics(base, overlay, connectivity);

    // 🏖️ 沙地生成调试信息
    console.log(`🗺️ 地图生成完成 (${W}x${H}):`, {
      水域密度: `${(waterDensity * 100).toFixed(0)}%`,
      沙地密度: `${(sandDensity * 100).toFixed(0)}%`,
      最终沙地覆盖率: `${(metrics.sandPct * 100).toFixed(1)}%`,
      沙地回退次数: attemptsS,
      最终密度: `${(densityS * 100).toFixed(0)}%`
    });

    return {
      baseLayer: base,
      overlayLayer: overlay,
      seed,
      connectivity,
      phaserData,
      metrics
    };
  }

  // ----------------- Helper: RNG -----------------
  private mulberry32(a: number): RNG {
    return () => {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  private rnd(): number { return this.rng(); }

  // ----------------- Helper: 2D utils -----------------
  private make2D<T>(h: number, w: number, val: T): T[][] {
    return Array.from({length:h}, ()=>Array(w).fill(val));
  }

  private coverage(mask: number[][]): number {
    const h=mask.length,w=mask[0].length; let c=0;
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) if (mask[y][x]===1) c++;
    return c/(w*h);
  }

  private inlandCoverage(mask: number[][]): number {
    const h=mask.length,w=mask[0].length; if (h<3||w<3) return 0;
    let c=0; const total=(h-2)*(w-2);
    for (let y=1;y<h-1;y++) for (let x=1;x<w-1;x++) if (mask[y][x]===1) c++;
    return c/total;
  }

  private maskOut(a: number[][], b: number[][]): number[][] {
    const h=a.length,w=a[0].length; const out=this.make2D(h,w,0);
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) out[y][x]=(a[y][x]===1 && b[y][x]===0)?1:0;
    return out;
  }

  // ----------------- CA generation -----------------
  private genMaskCA(w:number,h:number,density:number,smooth:number): number[][] {
    let m=this.make2D(h,w,0);
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) m[y][x]=(this.rnd()<density)?1:0;
    for (let i=0;i<smooth;i++) m=this.smooth(m);
    return m;
  }

  private smooth(m:number[][]): number[][] {
    const h=m.length,w=m[0].length; const out=this.make2D(h,w,0);
    for (let y=0;y<h;y++) for (let x=0;x<w;x++){
      let n=0; for (let dy=-1;dy<=1;dy++) for (let dx=-1;dx<=1;dx++){
        if (dx===0&&dy===0) continue; const nx=x+dx, ny=y+dy;
        if (nx>=0&&nx<w&&ny>=0&&ny<h) n+=m[ny][nx];
      }
      if (n>=5) out[y][x]=1; else if (n<=3) out[y][x]=0; else out[y][x]=m[y][x];
    }
    return out;
  }

  private removeSmallRegions(m:number[][], target=1): number[][] {
    const h=m.length,w=m[0].length; const out=this.make2D(h,w,0);
    const vis=this.make2D(h,w,false as any as boolean);
    const minSize=Math.max(8, Math.floor(w*h*0.01));
    const dirs=[[1,0],[-1,0],[0,1],[0,-1]];
    for (let y=0;y<h;y++) for (let x=0;x<w;x++){
      if (vis[y][x]) continue; if (m[y][x]!==target) { vis[y][x]=true; continue; }
      const stack:[[number,number]]=[ [x,y] ] as any; const region:[number,number][]=[];
      vis[y][x]=true;
      while (stack.length){
        const [cx,cy]=stack.pop()!; region.push([cx,cy]);
        for (const [dx,dy] of dirs){ const nx=cx+dx, ny=cy+dy; if (nx<0||nx>=w||ny<0||ny>=h) continue; if (vis[ny][nx]) continue; if (m[ny][nx]!==target) continue; vis[ny][nx]=true; stack.push([nx,ny]); }
      }
      if (region.length>=minSize) for (const [rx,ry] of region) out[ry][rx]=target;
    }
    return out;
  }

  private enforceMin2x2(m:number[][]): number[][] {
    const h=m.length,w=m[0].length; let changed=true; let iter=0;
    while(changed && iter<3){
      changed=false; iter++;
      const out=this.make2D(h,w,0);
      for (let y=0;y<h;y++) for (let x=0;x<w;x++){
        if (m[y][x]!==1) continue;
        const ok = this.belongsToAny2x2(m,x,y);
        if (ok) out[y][x]=1; else changed=true;
      }
      m=out;
    }
    return m;
  }

  private belongsToAny2x2(m:number[][], x:number,y:number): boolean {
    const h=m.length,w2=m[0].length;
    const is2x2=(sx:number,sy:number)=> sx>=0&&sy>=0&&sx+1<w2&&sy+1<h && m[sy][sx]&&m[sy][sx+1]&&m[sy+1][sx]&&m[sy+1][sx+1];
    const belongs = is2x2(x,y)||is2x2(x-1,y)||is2x2(x,y-1)||is2x2(x-1,y-1);

    // 🔧 额外检查: 防止单像素宽度对角通道
    if (belongs) {
      // 检查四周是否形成1像素宽的瓶颈
      const isFilled = (nx:number,ny:number) => nx>=0&&nx<w2&&ny>=0&&ny<h && m[ny][nx]===1;
      const n=isFilled(x,y-1), e=isFilled(x+1,y), s=isFilled(x,y+1), w=isFilled(x-1,y);
      const ne=isFilled(x+1,y-1), se=isFilled(x+1,y+1), sw=isFilled(x-1,y+1), nw=isFilled(x-1,y-1);

      // 禁止模式: 四周都有水，但所有对角线都没有水 (完全孤立的十字)
      if (n && e && s && w && !ne && !se && !sw && !nw) {
        return false; // 移除这个瓦片，避免单像素宽度
      }
    }

    return belongs;
  }

  // 🆕 移除单tile宽度河流/地形 - 确保所有形状最小宽度为2 tiles
  private removeNarrowRivers(m: number[][]): number[][] {
    const h = m.length, w = m[0].length;
    let changed = true;
    let iter = 0;

    while (changed && iter < 3) {
      changed = false;
      iter++;
      const out = this.make2D(h, w, 0);

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (m[y][x] !== 1) continue;

          // 检查当前tile是否会形成单tile宽度
          const isFilled = (nx: number, ny: number) => nx >= 0 && nx < w && ny >= 0 && ny < h && m[ny][nx] === 1;

          // 检查四个方向的邻居
          const n = isFilled(x, y - 1);
          const e = isFilled(x + 1, y);
          const s = isFilled(x, y + 1);
          const w_dir = isFilled(x - 1, y);

          // 检查对角线
          const ne = isFilled(x + 1, y - 1);
          const se = isFilled(x + 1, y + 1);
          const sw = isFilled(x - 1, y + 1);
          const nw = isFilled(x - 1, y - 1);

          // 判断是否形成单tile宽度
          let isSingleWidth = false;

          // 垂直单tile宽度 (上下有水，左右无水，且对角线也无水)
          if (n && s && !e && !w_dir && !ne && !nw && !se && !sw) {
            isSingleWidth = true;
          }
          // 水平单tile宽度 (左右有水，上下无水，且对角线也无水)
          else if (e && w_dir && !n && !s && !ne && !nw && !se && !sw) {
            isSingleWidth = true;
          }

          if (!isSingleWidth) {
            out[y][x] = 1;
          } else {
            changed = true;
          }
        }
      }
      m = out;
    }

    return m;
  }

  // ----------------- Ocean ring -----------------
  private addOceanRing(water:number[][]): void {
    const h=water.length,w=water[0].length;
    // outer ring water
    for (let x=0;x<w;x++){ water[0][x]=1; water[h-1][x]=1; }
    for (let y=0;y<h;y++){ water[y][0]=1; water[y][w-1]=1; }
    // inner isolation ring (plain)
    if (w>4 && h>4){
      for (let x=1;x<w-1;x++){ water[1][x]=0; water[h-2][x]=0; }
      for (let y=1;y<h-1;y++){ water[y][1]=0; water[y][w-2]=0; }
    }
  }

  // ----------------- Overlays -----------------
  private placeOverlays(base: BaseTerrainType[][], overlay: OverlayTerrainType[][]): void {
    const H=base.length,W=base[0].length;
    const isPlain=(x:number,y:number)=> base[y][x]==='PLAIN' && overlay[y][x]==='NONE';
    const total=W*H;
    const target=(pct:number)=> pct>0?Math.max(1, Math.floor(total*pct)):0;

    // defaults (as requested) - building01/building02/fence数量降低50%
    const mountainPct=0.05, forest01Pct=0.04, forest02Pct=0.04, building01Pct=0.0025, building02Pct=0.0025, fencePct=0.0025;

    const occupied=this.make2D(H,W,false as any as boolean);

    const place1x1=(type:OverlayTerrainType, count:number, onlyPlain=true)=>{
      if (count<=0) return;
      const cells:[number,number][]=[]; for (let y=1;y<H-1;y++) for (let x=1;x<W-1;x++) cells.push([x,y]);
      this.shuffle(cells);
      let placed=0;
      for (const [x,y] of cells){ if (placed>=count) break; if (onlyPlain && !isPlain(x,y)) continue; if (occupied[y][x]) continue; overlay[y][x]=type; occupied[y][x]=true; placed++; }
    };

    const placeCluster=(type:OverlayTerrainType, count:number)=>{
      if (count<=0) return;
      const candidates:[number,number][]=[]; for (let y=1;y<H-1;y++) for (let x=1;x<W-1;x++) if (isPlain(x,y) && !occupied[y][x]) candidates.push([x,y]);
      this.shuffle(candidates);
      const clusters=Math.max(1, Math.floor(count/8));
      const centers=candidates.slice(0,clusters);
      let remain=count; const R=3;
      for (const [cx,cy] of centers){ if (remain<=0) break; for (let dy=-R;dy<=R;dy++) for (let dx=-R;dx<=R;dx++){
        if (remain<=0) break; const x=cx+dx, y=cy+dy; if (x<=0||x>=W-1||y<=0||y>=H-1) continue; if (!isPlain(x,y)||occupied[y][x]) continue; const dist=Math.hypot(dx,dy); if (dist>R) continue; const p=Math.max(0.15, 1-dist/R); if (this.rng()<p){ overlay[y][x]=type; occupied[y][x]=true; remain--; }
      }}
      if (remain>0) place1x1(type, remain, true);
    };

    placeCluster('MOUNTAIN', target(mountainPct)); // 只在平原上生成山地
    placeCluster('FOREST01', target(forest01Pct)); // 避开山地和水域/沙地
    placeCluster('FOREST02', target(forest02Pct)); // 避开山地和水域/沙地
    place1x1('BUILDING01', target(building01Pct), true); // 避开山地和水域/沙地
    place1x1('BUILDING02', target(building02Pct), true); // 避开山地和水域/沙地
    place1x1('FENCE', target(fencePct), true); // 避开山地和水域/沙地
  }

  // ----------------- Connectivity fix -----------------
  private fixConnectivity(base: BaseTerrainType[][], overlay: OverlayTerrainType[][]): void {
    const H=base.length,W=base[0].length;
    const isMountain=(x:number,y:number)=> overlay[y][x]==='MOUNTAIN';
    const passable=(x:number,y:number)=> (base[y][x]==='PLAIN' || base[y][x]==='SAND') && !isMountain(x,y);
    const inBounds=(x:number,y:number)=> x>0 && x<W-1 && y>0 && y<H-1; // avoid boundary

    const visited=this.make2D(H,W,false as any as boolean);
    const comps:number[][][]=[];
    const dirs=[[1,0],[-1,0],[0,1],[0,-1]];
    for (let y=1;y<H-1;y++) for (let x=1;x<W-1;x++){
      if (visited[y][x]||!passable(x,y)) continue; const q:[[number,number]]=[[x,y]] as any; visited[y][x]=true; const cur:number[][]=[];
      while(q.length){ const [cx,cy]=q.shift()!; cur.push([cx,cy]); for (const [dx,dy] of dirs){ const nx=cx+dx, ny=cy+dy; if(!inBounds(nx,ny)) continue; if(!visited[ny][nx] && passable(nx,ny)){ visited[ny][nx]=true; q.push([nx,ny]); } } }
      comps.push(cur);
    }
    if (comps.length<=1) return;
    comps.sort((a,b)=>b.length-a.length);
    const mainMask=this.make2D(H,W,false as any as boolean); for (const [x,y] of comps[0]) mainMask[y][x]=true;

    // Connect each other component to main by carving minimal-cost path
    const INF=1e9; const cost=(x:number,y:number)=>{
      if (!inBounds(x,y)) return INF;
      if (passable(x,y)) return 1;
      if (isMountain(x,y)) return 3; // prefer removing mountain
      if (base[y][x]==='WATER') return 6; // avoid water unless needed
      return 2;
    };

    function dijkstra(from:number[][]): [number,number][][] {
      const dist:number[][]=Array.from({length:H},()=>Array(W).fill(INF));
      const prev:( [number,number] | null )[][]=Array.from({length:H},()=>Array(W).fill(null));
      const open:[number,number][]=[];
      for (const [sx,sy] of from){ dist[sy][sx]=0; open.push([sx,sy]); }
      while(open.length){
        let bi=0; for (let i=1;i<open.length;i++){ const [ox,oy]=open[i]; const [bx,by]=open[bi]; if (dist[oy][ox]<dist[by][bx]) bi=i; }
        const [cx,cy]=open.splice(bi,1)[0];
        if (mainMask[cy][cx]){
          // reconstruct path
          const path:[number,number][]=[]; let px=cx, py=cy; path.push([px,py]);
          while(prev[py][px]){ const [ppx,ppy]=prev[py][px]!; path.push([ppx,ppy]); px=ppx; py=ppy; }
          return [path];
        }
        for (const [dx,dy] of dirs){ const nx=cx+dx, ny=cy+dy; const nd=dist[cy][cx]+cost(nx,ny); if (nd<dist[ny][nx]){ dist[ny][nx]=nd; prev[ny][nx]=[cx,cy]; open.push([nx,ny]); } }
      }
      return [];
    }

    for (let i=1;i<comps.length;i++){
      const paths=dijkstra(comps[i]); if (!paths.length) continue; const path=paths[0];
      // Carve passable corridor
      for (const [x,y] of path){
        if (overlay[y][x]==='MOUNTAIN') overlay[y][x]='NONE';
        if (base[y][x]==='WATER'){
          // clear a 2x2 block aligned to even grid when possible
          if (x>1 && x<W-2 && y>1 && y<H-2){
            const ax = (x%2===0)?x:x-1; const ay=(y%2===0)?y:y-1;
            for (let dy=0;dy<2;dy++) for (let dx=0;dx<2;dx++) base[ay+dy][ax+dx]='PLAIN';
          } else {
            base[y][x]='PLAIN';
          }
        }
      }
      // Update mainMask
      const vis2=this.make2D(H,W,false as any as boolean); const q:[[number,number]]=[[comps[0][0][0], comps[0][0][1]]] as any; vis2[q[0][1]][q[0][0]]=true; mainMask[q[0][1]][q[0][0]]=true;
      while(q.length){ const [cx,cy]=q.shift()!; for (const [dx,dy] of dirs){ const nx=cx+dx, ny=cy+dy; if(!inBounds(nx,ny)) continue; if(!vis2[ny][nx] && ((base[ny][nx]!=='WATER') && overlay[ny][nx]!=='MOUNTAIN')){ vis2[ny][nx]=true; mainMask[ny][nx]=true; q.push([nx,ny]); } } }
    }
  }

  // ----------------- Metrics & phaser data -----------------
  private computeConnectivity(base: BaseTerrainType[][], overlay: OverlayTerrainType[][]){
    const H=base.length,W=base[0].length; const inBounds=(x:number,y:number)=>x>=0&&x<W&&y>=0&&y<H;
    const pass=(x:number,y:number)=> (base[y][x]!=='WATER') && (overlay[y][x]!=='MOUNTAIN');
    const vis=this.make2D(H,W,false as any as boolean); const comps:number[]=[];
    const dirs=[[1,0],[-1,0],[0,1],[0,-1]];
    for (let y=0;y<H;y++) for (let x=0;x<W;x++) if (!vis[y][x] && pass(x,y)){
      let c=0; const q:[[number,number]]=[[x,y]] as any; vis[y][x]=true; while(q.length){ const [cx,cy]=q.shift()!; c++; for (const [dx,dy] of dirs){ const nx=cx+dx, ny=cy+dy; if(!inBounds(nx,ny)) continue; if(!vis[ny][nx]&&pass(nx,ny)){vis[ny][nx]=true; q.push([nx,ny]);} } }
      comps.push(c);
    }
    const total= W*H - this.countTiles(base,'WATER') - this.countOverlay(overlay,'MOUNTAIN');
    const largest = comps.length? Math.max(...comps):0;
    return { passableRatio: total>0? (comps.reduce((a,b)=>a+b,0)/ (W*H)):0, largestComponentRatio: total>0? (largest/Math.max(1,total)):1 };
  }

  private buildPhaserData(base: BaseTerrainType[][], overlay: OverlayTerrainType[][]){
    const H=base.length,W=base[0].length;
    const baseTileIndices:number[]=[]; const overlayTileIndices:number[]=[];
    for (let y=0;y<H;y++) for (let x=0;x<W;x++){
      const b=base[y][x];
      baseTileIndices.push(b==='PLAIN'?BaseTerrainIndex.PLAIN: b==='WATER'?BaseTerrainIndex.WATER: BaseTerrainIndex.SAND);
      const o=overlay[y][x];
      overlayTileIndices.push(o==='NONE'?OverlayTerrainIndex.NONE:
        o==='FOREST01'?OverlayTerrainIndex.FOREST01:
        o==='FOREST02'?OverlayTerrainIndex.FOREST02:
        o==='MOUNTAIN'?OverlayTerrainIndex.MOUNTAIN:
        o==='BUILDING01'?OverlayTerrainIndex.BUILDING01:
        o==='BUILDING02'?OverlayTerrainIndex.BUILDING02:
        OverlayTerrainIndex.FENCE);
    }
    return {
      baseTileIndices,
      overlayTileIndices,
      tilesetMapping: {
        base: { PLAIN: 'plain', WATER: 'water', SAND: 'sand' },
        overlay: {
          FOREST01: 'forest01', FOREST02: 'forest02', MOUNTAIN: 'mountain', BUILDING01: 'building01', BUILDING02: 'building02', FENCE: 'fence'
        }
      }
    };
  }

  private computeMetrics(base: BaseTerrainType[][], overlay: OverlayTerrainType[][], conn: {passableRatio:number, largestComponentRatio:number}){
    const H=base.length,W=base[0].length; const total=W*H;
    const water=this.countTiles(base,'WATER')/total;
    const sand=this.countTiles(base,'SAND')/total;
    const forest01=this.countOverlay(overlay,'FOREST01')/total;
    const forest02=this.countOverlay(overlay,'FOREST02')/total;
    const mountain=this.countOverlay(overlay,'MOUNTAIN')/total;
    const building01=this.countOverlay(overlay,'BUILDING01')/total;
    const building02=this.countOverlay(overlay,'BUILDING02')/total;
    const fence=this.countOverlay(overlay,'FENCE')/total;
    return {
      waterPct: water,
      sandPct: sand,
      forest01Pct: forest01,
      forest02Pct: forest02,
      mountainPct: mountain,
      building01Pct: building01,
      building02Pct: building02,
      fencePct: fence,
      passableLargestRatio: conn.largestComponentRatio
    };
  }

  private countTiles(base: BaseTerrainType[][], t: BaseTerrainType){
    const H=base.length,W=base[0].length; let c=0; for (let y=0;y<H;y++) for (let x=0;x<W;x++) if (base[y][x]===t) c++; return c;
  }
  private countOverlay(overlay: OverlayTerrainType[][], t: OverlayTerrainType){
    const H=overlay.length,W=overlay[0].length; let c=0; for (let y=0;y<H;y++) for (let x=0;x<W;x++) if (overlay[y][x]===t) c++; return c;
  }

  private shuffle<T>(arr:T[]):void{ for (let i=arr.length-1;i>0;i--){ const j=Math.floor(this.rnd()*(i+1)); [arr[i],arr[j]]=[arr[j],arr[i]]; } }
}
